// pruebaAsistente.utils.ts — los textos de «Probar el asistente» (round 2026-10-31). Funciones puras: decidir QUÉ decir y CUÁNTO de grave es cada cosa
// vive aquí y no en el componente, para probarlo sin renderizar nada.
//
// La prueba comprueba, sin guardar nada, lo que el administrador tiene ESCRITO en «Modelos y límites»: que el servicio del bot tenga la clave de OpenAI y
// pueda hablar con el servidor, y que el modelo de cada tarea exista y sirva. El servidor nunca manda texto de OpenAI: manda códigos (`resultado`) y frases
// fijas (`detalle`); todo lo que la persona lee se arma aquí, en español neutro y sin tecnicismos, con el consejo de qué hacer.
//
// NO se escriben aquí nombres de variables de entorno: las que faltan llegan en los datos (`faltan`) y se muestran tal cual.
import type { NombreTareaIA, PruebaAsistenteFallo, PruebaAsistenteResultado, PruebaAsistenteTarea, PruebaAsistenteTareas } from "../../../interfaces/botAsistente.interfaces";
import { formatoDuracion } from "./botAsistente.utils";

export type NivelPrueba = "ok" | "aviso" | "error";

export interface LineaPrueba {
  /** Estable, para las pruebas y como `key`. */
  clave: string;
  nivel: NivelPrueba;
  texto: string;
  /** Qué hacer, en una frase (opcional). */
  ayuda?: string;
}

export const TITULO_TAREA: Record<NombreTareaIA, string> = {
  "gasto.parse": "Leer un gasto nuevo",
  "gasto.correct": "Corregir un gasto",
};

/**
 * Color del icono de cada nivel. Un icono es un elemento gráfico que lleva significado: pide 3:1 de contraste con el fondo blanco (WCAG 1.4.11). El ámbar
 * que AntD usa por defecto (#d48806) daba 2,87:1 y no llegaba; este da 3,76:1. `pruebaAsistente.utils.test.ts` calcula el contraste de los tres.
 */
export const COLOR_ICONO_PRUEBA: Record<NivelPrueba, string> = {
  ok: "#389e0d",
  aviso: "#b87500",
  error: "#cf1322",
};

/** Lo que dice el botón y lo que anuncia el lector de pantalla. */
export const TEXTO_BOTON_PROBAR = "Probar el asistente";
export const TEXTO_PROBANDO = "Probando el asistente con OpenAI… puede tardar hasta 40 segundos.";
export const TEXTO_FORMULARIO_CON_ERRORES = "Corrige los campos marcados en rojo para poder probar.";
export const MODELO_SUGERIDO = "gpt-4.1-mini";

/** El nombre del modelo entre comillas latinas, como se ve en el resto del panel. */
const m = (modelo: string): string => `«${modelo}»`;

/** Quita el punto final de una frase para poder seguir escribiendo después de ella. */
const sinPunto = (frase: string): string => frase.replace(/[.\s]+$/u, "");

/** «A», «A y B», «A, B y C». */
export function listaConY(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}`;
}

/**
 * ¿La respuesta del modelo se cortó antes de terminar? El servicio del bot lo dice con una frase fija («La respuesta se cortó antes de terminar: revisa el
 * máximo de tokens.», twilio/src/probarAsistente.js, `DETALLES.incompleto`) bajo el resultado «no_sirve». Se reconoce por esa frase porque el resultado no
 * distingue el caso; `pruebaAsistente.utils.test.ts` lee la frase real del servicio y falla si deja de reconocerse.
 */
export const esRespuestaCortada = (detalle: string): boolean => /se cort[óo] antes de terminar/i.test(detalle);

/** Qué pasa con el modelo de respaldo (el que el asistente usa si el elegido no existe). */
function fraseDeRespaldo(t: PruebaAsistenteTarea): string {
  if (t.respaldo === undefined) return "Tampoco hay otro modelo de respaldo: mientras siga así, el asistente no podrá leer mensajes.";
  return t.respaldo.ok
    ? `Si lo dejas así, el asistente usará ${m(t.respaldo.modelo)}, que sí funciona.`
    : `Si lo dejas así, el asistente intentará usar ${m(t.respaldo.modelo)}, pero tampoco funcionó.`;
}

/** Las líneas de UNA tarea (la principal y, si corresponde, un aviso aparte sobre la temperatura). */
export function lineasDeTarea(nombre: NombreTareaIA, t: PruebaAsistenteTarea, pedida?: { temperature: number | null }): LineaPrueba[] {
  const titulo = TITULO_TAREA[nombre];
  const modelo = m(t.modelo);
  const linea = (nivel: NivelPrueba, texto: string, ayuda?: string): LineaPrueba => ({ clave: `${nombre}.${t.resultado}`, nivel, texto: `${titulo}: ${texto}`, ...(ayuda ? { ayuda } : {}) });
  const lineas: LineaPrueba[] = [];

  switch (t.resultado) {
    case "ok": {
      const hecho = nombre === "gasto.parse" ? "entendió el gasto de ejemplo" : "aplicó bien la corrección de ejemplo";
      lineas.push(linea("ok", `el modelo ${modelo} existe y ${hecho} (${formatoDuracion(t.ms)}).`));
      break;
    }
    case "modelo_inexistente":
      lineas.push(linea("error", `el modelo ${modelo} no existe en OpenAI.`, `Revisa que esté escrito igual que en la lista de OpenAI. ${fraseDeRespaldo(t)}`));
      break;
    case "sin_acceso":
      lineas.push(linea("error", `el modelo ${modelo} existe, pero la cuenta de OpenAI del asistente no tiene acceso a él.`, `Pide acceso a ese modelo en OpenAI o elige otro. ${fraseDeRespaldo(t)}`));
      break;
    case "clave_invalida":
      lineas.push(linea("error", "OpenAI no aceptó la clave del servicio del bot.", "Revísala en las variables de ese servicio en Railway."));
      break;
    case "sin_clave":
      lineas.push(linea("aviso", `no se pudo probar el modelo ${modelo}: al servicio del bot le falta la clave de OpenAI.`));
      break;
    case "limite":
      lineas.push(linea("aviso", `OpenAI pidió esperar (límite de uso o saldo agotado), así que no se pudo probar el modelo ${modelo}.`, "Revisa tu cuenta de OpenAI y vuelve a probar."));
      break;
    case "tiempo":
      lineas.push(linea("aviso", `OpenAI no respondió a tiempo con el modelo ${modelo}.`, "Vuelve a probar; si se repite, prueba con otro modelo."));
      break;
    case "red":
      lineas.push(linea("aviso", "el servicio del bot no pudo conectarse con OpenAI.", "Vuelve a probar en un momento."));
      break;
    case "no_sirve":
      // Una respuesta cortada NO es culpa del modelo: el tope de tokens guardado es muy bajo para lo que el asistente debe contestar. Decirle que elija otro
      // modelo (como en los demás «no sirve») mandaba a la persona a cambiar lo que funcionaba. Y como la prueba usa el tope GUARDADO (no el que está escrito
      // en el formulario), subirlo en pantalla no basta: hay que guardar y volver a probar.
      if (esRespuestaCortada(t.detalle)) {
        lineas.push(
          linea(
            "error",
            `el modelo ${modelo} respondió, pero la respuesta se cortó antes de terminar.`,
            "No es un problema del modelo: la respuesta no cabe en el «Máximo de tokens de respuesta» guardado. Súbelo (o marca «Sin tope»), guarda y vuelve a probar: la prueba usa el máximo que está guardado, no el que tienes escrito arriba.",
          ),
        );
        break;
      }
      lineas.push(linea("error", `el modelo ${modelo} existe, pero no sirve para esto.`, `${t.detalle ? `${sinPunto(t.detalle)}. ` : ""}Elige otro modelo (por ejemplo ${m(MODELO_SUGERIDO)}).`));
      break;
    default:
      lineas.push(linea("aviso", `la prueba del modelo ${modelo} falló por un error de OpenAI.`, `${t.detalle ? `${sinPunto(t.detalle)}. ` : ""}Vuelve a probar.`));
  }

  // La temperatura solo importa si el modelo respondió bien: si no, no se puede saber.
  if (t.resultado === "ok") {
    if (t.temperaturaRechazada) {
      lineas.push({
        clave: `${nombre}.temperatura`,
        nivel: "aviso",
        texto: `${titulo}: el modelo ${modelo} no acepta la temperatura, así que se enviará sin ella.`,
        ayuda: "No pasa nada: el asistente ya lo hace solo. Si quieres, marca «No enviar temperatura» para dejarlo claro.",
      });
    } else if (pedida && pedida.temperature !== null && !t.temperaturaEnviada) {
      lineas.push({
        clave: `${nombre}.temperatura`,
        nivel: "aviso",
        texto: `${titulo}: el modelo ${modelo} no usa la temperatura, así que el asistente no se la enviará.`,
        ayuda: "Está bien. Puedes marcar «No enviar temperatura» para dejarlo claro.",
      });
    }
  }
  return lineas;
}

/** La zona horaria que el servicio del bot DEBE tener (las fechas de los gastos se calculan con ella). */
export const ZONA_HORARIA_ESPERADA = "America/Santiago";

/** Todas las líneas de una prueba, en el orden en que se muestran. */
export function lineasDePrueba(datos: PruebaAsistenteResultado, pedidas: PruebaAsistenteTareas = {}): LineaPrueba[] {
  const lineas: LineaPrueba[] = [{ clave: "servicio.responde", nivel: "ok", texto: "El servicio del bot responde." }];

  lineas.push(
    datos.servicio.openaiConfigurada
      ? { clave: "servicio.openai", nivel: "ok", texto: "El servicio del bot tiene la clave de OpenAI." }
      : {
          clave: "servicio.openai",
          nivel: "error",
          texto: "Al servicio del bot le falta la clave de OpenAI.",
          ayuda: "Agrégala en las variables de ese servicio en Railway; sin ella el asistente no puede leer mensajes.",
        },
  );

  const acceso = datos.servicio.accesoAlServidor;
  if (acceso.ok) {
    lineas.push({ clave: "servicio.acceso", nivel: "ok", texto: "El bot puede hablar con el servidor." });
  } else {
    const faltan = acceso.faltan;
    lineas.push({
      clave: "servicio.acceso",
      nivel: "error",
      texto: faltan.length > 0 ? `El bot no puede hablar con el servidor: falta definir o corregir ${listaConY(faltan)} en el servicio del bot.` : "El bot no puede hablar con el servidor.",
      ayuda: faltan.length > 1 ? "Agrega esas variables en el servicio del bot, en Railway, y espera a que se vuelva a desplegar." : "Agrega esa variable en el servicio del bot, en Railway, y espera a que se vuelva a desplegar.",
    });
  }

  if (datos.servicio.zonaHoraria !== null && datos.servicio.zonaHoraria !== ZONA_HORARIA_ESPERADA) {
    lineas.push({
      clave: "servicio.zona",
      nivel: "aviso",
      texto: "El servicio del bot no tiene la zona horaria de Chile.",
      ayuda: "Sin ella, los gastos que se registran de noche pueden quedar con la fecha de mañana. Corrígela en las variables de ese servicio en Railway.",
    });
  }

  for (const nombre of ["gasto.parse", "gasto.correct"] as const) {
    const t = datos.tareas[nombre];
    if (t) lineas.push(...lineasDeTarea(nombre, t, pedidas[nombre]));
  }
  return lineas;
}

export interface ResumenPrueba {
  nivel: NivelPrueba;
  texto: string;
}

/** El veredicto de arriba de la lista, calculado con lo que se ve (no solo con el `ok` del servidor). */
export function resumenDePrueba(lineas: readonly LineaPrueba[]): ResumenPrueba {
  if (lineas.some((l) => l.nivel === "error")) return { nivel: "error", texto: "Hay cosas por corregir antes de contar con el asistente." };
  // Un aviso «leve» (la temperatura que el modelo no usa, la zona horaria) no impide que la prueba valga; los demás avisos son que algo NO se
  // pudo probar (sin clave, límite de OpenAI, tiempo, red, error): el titular no puede decir «se pudo probar» o la persona se queda con un
  // amarillo y cree que pasó con detalles (revisor de uso, 2026-10-31).
  const AVISO_LEVE = /\.(temperatura|zona)$/;
  const avisos = lineas.filter((l) => l.nivel === "aviso");
  if (avisos.some((l) => !AVISO_LEVE.test(l.clave))) return { nivel: "aviso", texto: "No se pudo probar todo: revisa los avisos y vuelve a probar." };
  if (avisos.length > 0) return { nivel: "aviso", texto: "Se pudo probar, pero hay avisos para revisar." };
  return { nivel: "ok", texto: "Todo salió bien con los valores escritos arriba." };
}

/** Lo que se anuncia al terminar (corto, para el lector de pantalla). */
export function anuncioDePrueba(resumen: ResumenPrueba): string {
  return `Prueba terminada. ${resumen.texto}`;
}

/**
 * Por qué no se pudo hacer la prueba, en lenguaje sencillo. Para un 424 (el servidor sí respondió, pero algo de su lado falló: variables, el servicio del
 * bot...) se muestra el mensaje que dio el servidor, porque nombra la variable concreta que hay que revisar.
 */
export function textoDelFallo(fallo: PruebaAsistenteFallo): { titulo: string; detalle?: string } {
  const { status, code, message } = fallo;
  if (status === 403) return { titulo: "No tienes permiso para probar el asistente.", detalle: "Solo los administradores pueden hacerlo." };
  if (status === 409 || code === "PRUEBA_EN_CURSO") return { titulo: "Ya hay una prueba en curso.", detalle: "Espera unos segundos y vuelve a intentarlo." };
  if (status === 429 || code === "PRUEBA_LIMITE") return { titulo: "Se hicieron demasiadas pruebas seguidas.", detalle: "Espera un minuto y vuelve a intentarlo." };
  if (status === 424) {
    return { titulo: "No se pudo completar la prueba.", detalle: message || "El servicio del bot no está disponible. Revisa su configuración." };
  }
  if (status === 400) {
    return { titulo: "Los valores escritos no son válidos.", detalle: message || "Corrige los campos del formulario y vuelve a probar." };
  }
  if (status === 404) {
    return { titulo: "Esta versión del servidor todavía no tiene la prueba.", detalle: "Espera a que termine el despliegue y vuelve a intentarlo." };
  }
  if (code === "RESPUESTA_INVALIDA") {
    return { titulo: "La respuesta del servidor no se entiende.", detalle: "Puede que el servidor y la pantalla estén en versiones distintas. Recarga la página y vuelve a probar." };
  }
  if (status === null) return { titulo: "No se pudo conectar con el servidor.", detalle: "Revisa tu conexión y vuelve a intentarlo." };
  return { titulo: "No se pudo hacer la prueba.", detalle: "El servidor tuvo un problema. Vuelve a intentarlo en un momento." };
}

/**
 * La «huella» de lo que se prueba: cambia si cambia cualquier modelo o temperatura. Sirve para saber si un resultado sigue valiendo para lo que hay
 * escrito ahora (si la persona vuelve al valor de antes, la huella vuelve a coincidir y el resultado vuelve a valer).
 */
export function huellaDePrueba(tareas: PruebaAsistenteTareas): string {
  return JSON.stringify(
    (["gasto.parse", "gasto.correct"] as const).map((n) => [n, tareas[n]?.model ?? null, tareas[n]?.temperature ?? null]),
  );
}
